import Vue from 'vue';
import Component from 'vue-class-component';
import Moment from 'moment';
import { Watch } from 'vue-property-decorator';
import EdSubjectCalendar from '@/courses/components/ed-subject-builder/ed-subject-calendar/ed-subject-calendar.component.vue';
import EdCreateProductComponent from '@/courses/components/ed-create-product/ed-create-product.component.vue';
import { ALL_CONTENT_ROUTES } from '@/app/private/my-content/views/all/all.router';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { BUILDER_CALENDAR_TYPES, BUILDER_MENU, BUILDER_EVENT_TYPES, BUILDER_RULES, BUILDER_DAY_OPTIONS } from '@/courses/values/courses.values';
import { COURSE_TEMPLATE_EVENTS, PRODUCT_TYPES } from '@/course-templates/values/course-templates.values';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { EdTabsHeaderOption } from '@/models/ed-tabs-header-option.model';
import { Status } from '@/models/status.model';
import { CourseService } from '@/courses/services/courses.service';
import KEYS from '@/values/keys';
import { IParamsCreateComponent } from '@/courses/interfaces/params-create-component.interface';
import { CONTENT_TYPE } from '@/classes/values/classes.values';
import { CourseTemplateService } from '@/course-templates/services/course-templates.service';
import { TOOLS_ROUTES } from '@/tools/tools.router';
import EdStepper from '@/components/common/ed-stepper/ed-stepper.vue';

@Component({
  name: 'subject-builder',
  components: {
    EdSubjectCalendar,
    EdCreateProductComponent,
    EdSubjectBuildingProcess: () => import(/* EdSubjectBuildingProcess */'@/courses/components/ed-subject-builder/ed-subject-building-process/ed-subject-building-process.component.vue'),
    EdSubjectBuilderSidebar: () => import(/* EdSubjectBuilderSidebar */'@/app/private/subjects/components/ed-subject-builder-sidebar/ed-subject-builder-sidebar.component.vue'),
    EdSubjectBuilderRules: () => import(/* EdSubjectBuilderRules */'@/courses/components/ed-subject-builder/ed-subject-builder-rules/ed-subject-builder-rules.component.vue'),
    EdSubjectBuilderRulesList: () => import(/* EdSubjectBuilderRulesList */'@/courses/components/ed-subject-builder/ed-subject-builder-rules-list/ed-subject-builder-rules-list.component.vue'),
    EdStepper,
  },
})
export default class SubjectBuilderView extends Vue {

  public status: Status = new Status();
  public showSpinner: boolean = true;
  public step: number = 1;
  public calendarTypes: any = BUILDER_CALENDAR_TYPES;
  public productType: any = PRODUCT_TYPES.BUILDER;
  public template: any = {};
  public disabledSubjectForm = false
  public menu: EdTabsHeaderOption[];
  public isTabMenuDisabled = false;
  public isButtonHidden = false;
  public buildingLog: string = '';
  public contentId: number = 0;
  public calendarType: string = '';
  public isEditionMode: boolean = false;
  public isSidebarOpened = false;
  public builder: any = {};
  public callBackup: any = {
    funDayoffBackup: false,
    prevFunDayOffDate: '',
    teachingDaysBackup: false,
    prevTeachingDays: [],
    prevResourceId: ''
  }
  public addRule: boolean = false;
  public ruleOptions: any = null;
  public templateIdFromQuickSubject = null;

  /* Computed */
  get isCalendarStep() {
    return this.calendarType == this.calendarTypes.CALENDAR;
  }

  get isLessonStep() {
    return this.calendarType == this.calendarTypes.LESSONS;
  }

  get quickContentId(): number {
    return this.isQuickSubject ? Number(this.$route.params.id) : null;
  }

  get templateId(): number {
    if (this.isQuickSubject && !!this.templateIdFromQuickSubject) {
      return this.templateIdFromQuickSubject;
    }

    return  Number(this.$route.params.id);
  }

  get type(): string {
    return this.$route?.query?.type as string || CONTENT_TYPE.TEMPLATE;
  }

  get isQuickSubject(): boolean {
    return this.type === CONTENT_TYPE.QUICK_SUBJECT;
  }

  get organizationId() {
    let institutions: any[] = this.$store.state.ToolsStore.institutionInfo;
    return institutions.length > 0 ? institutions[0].organizationId : null;
  }

  get startDateCalendar(): string {
    let date = this.schoolDay.startDate;

    return date?.substring(0, date.length - 2)+'01';
  }

  get endDateCalendar(): string {
    let date = this.schoolDay.endDate;
    let endOfMonth = Moment(date).clone().endOf('month').format('DD').toString();

    return date?.substring(0, date.length - 2)+endOfMonth;
  }

  get lessons(): any[] {
    return this.builder?.lessons || [];
  }

  get specialDays(): any[] {
    return this.builder?.specialDays || [];
  }

  get workingDays(): any[] {
    return this.builder?.workingDays || [];
  }

  get additionalLessons(): any[] {
    return this.builder?.additionalLessons || [];
  }

  get schoolDay() {
    return this.builder?.schoolDay || {};
  }

  get existFunLessons(): number {
    return this.builder?.existFunLessons || 0;
  }

  get subject(): number {
    return this.builder?.subject || {};
  }

  get coverImage(): string {
    return this.builder?.coverImage || '';
  }

  get rulesApplied(): any {
    return this.builder?.rulesApplied || [];
  }

  get rulesAppliedList(): any {
    return this.rulesApplied.filter(el => el.type === BUILDER_RULES.PLACE_LABELS_ON_WORKING_DAY || el.type === BUILDER_RULES.START_LESSON) || [];
  }
  
  get objectsTagged(): any {
    return this.builder?.objectsTagged || [];
  }

  get automaticFolderType(): string {
    return this.builder?.automaticFolderType || null;
  }

  get componentsSelected(): IParamsCreateComponent[] {
    return this.builder?.componentsSelected || [];
  }

  get displayTypes() {
    const funLabel = this.isLessonStep ? 'funLesson' : 'fun';

    const types = [{
      label: this.$t('courses.subjectBuilder.contentLesson'),
      value: BUILDER_EVENT_TYPES.CONTENT,
      className: 'legend-lesson-content',
    },{
      label: this.$t('courses.subjectBuilder.fillerLesson'),
      value: BUILDER_EVENT_TYPES.FILLER,
      className: 'legend-lesson-filler',
    },{
      label: this.$t('courses.subjectBuilder.holiday'),
      value: BUILDER_EVENT_TYPES.HOLIDAY,
      className: 'legend-holiday',
    },{
      label: this.$t('courses.subjectBuilder.dayOff'),
      value: BUILDER_EVENT_TYPES.DAY_OFF,
      className: 'legend-day-off',
    },{
      label: this.$t(`courses.subjectBuilder.${funLabel}`),
      value: BUILDER_EVENT_TYPES.FUN,
      className: 'legend-lesson-fun',
    }];

    return this.isCalendarStep
      ? types.filter(el => el.value !== BUILDER_EVENT_TYPES.CONTENT && el.value !==  BUILDER_EVENT_TYPES.FILLER) 
      : types.filter(el => el.value !== BUILDER_EVENT_TYPES.HOLIDAY && el.value !==  BUILDER_EVENT_TYPES.DAY_OFF);
  }

  get calendarComponent() {
    const subjectCalendarLesson: any = this.$refs?.subjectCalendarLesson;
    const subjectCalendarDates: any = this.$refs?.subjectCalendarDates;

    return this.isLessonStep ? subjectCalendarLesson : subjectCalendarDates;
  }

  get allLessons() {
    return this.builder?.allLessons || [];
  }

  public goBack() {
    if(this.isQuickSubject) {
      this.$router.push({ name: 'class-admin' });
    } else {
      this.$router.push({ name: ALL_CONTENT_ROUTES.TEMPLATES.NAME });
    }
  }

  async getTemplateDescription() {
    if (this.templateId) {
      try {
        if (this.isQuickSubject) {
          await this.setTemplateDescriptionFromQuickSubject(this.templateId);
        } else {
          await this.setTemplateDescriptionFromTemplate(this.templateId);
        }
      } catch (err){
        this.$EdToast.create({ 
          msg:  `${this.$t('courseTemplates.errors.errorLoadingCourseTemplate')}`,
          type: TOAST_TYPES.DANGER,
          top: '3rem',
          timeout: 3000
        });
      }
    }
  }

  async setTemplateDescriptionFromTemplate(templateId: number) {
    const response = await CourseTemplateService.getCourseTemplate(templateId);

    this.template = {
      id: response.contentId,
      title: response.contentTitle,
      img: response.coverThumbnailSmallUrl,
      defaultDueDateForTasks: response.defaultDueDateForTasks,
      defaultLastDateForTasks: response.defaultLastDateForTasks,
      contentSummary: response.contentSummary,
      categoryId: response.category.id,
      imgHash: response.coverImg,
      hasGrading: response.hasGrading,
      actualGrade: response.actualGrade,
      suggestedGrade: response.suggestedGrade,
      schoolDay: response.schoolDay,
      duration: response.duration,
      components: response.components
    }
  }

  async setTemplateDescriptionFromQuickSubject(quickContentId: number) {
    const {
      subject,
      templateId,
      schoolDay,
      componentsSelected,
      automaticFolderType,
    } = await CourseTemplateService.getQuickSubjectInfo(quickContentId);
    
    this.templateIdFromQuickSubject = templateId;
    this.template = {
      id: templateId,
      title: subject.contentTitle,
      img: subject.coverThumbnailSmallUrl,
      defaultDueDateForTasks: subject.defaultDueDateForTasks,
      defaultLastDateForTasks: subject.defaultLastDateForTasks,
      contentSummary: subject.contentSummary,
      categoryId: subject.category.id,
      imgHash: subject.coverImg,
      hasGrading: subject.hasGrading,
      actualGrade: subject.actualGrade,
      suggestedGrade: subject.suggestedGrade,
      schoolDay: schoolDay,
      duration: subject.duration,
      components: componentsSelected,
      automaticFolderType,
    };
  }

  async confirmRunBuilder() {
    this.$EdModal.createConfirmationModal({
      body: this.$i18n.t('courses.subjectBuilder.confirmDone'),
      action: this.runBuilder.bind(this)
    });
  }

  async runBuilder() {
    try {
      const subjectCalendarLesson: any = this.$refs?.subjectCalendarLesson;
      const subjectBuildingProcess: any = this.$refs?.subjectBuildingProcess;
      
      this.isTabMenuDisabled = true;

      const response = await CourseService.runSubjectBuilder({
        ...subjectCalendarLesson.calendarState,
        templateId: this.templateId,
        quickContentId: this.quickContentId,
      });

      this.contentId = response.id;
      this.step = 4;

      subjectBuildingProcess?.getLogs(this.contentId);
    } catch(err) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('myContent.contentItem.errorMessage'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    }
  }

  saveSubjectDescriptionClick() {
    this.$edEventBus.$emit(COURSES_EVENTS.SUBJECT_DESCRIPTION_BUILDER);
  }
  
  async saveSubjectDescription({formData, uploadedImage}) {
    try {
      this.status.setLoading();
      this.template = {
        ...formData,
        title: formData.contentTitle,
        img: formData.coverImageUrl,
        defaultDueDateForTasks: formData.defaultDueDateForTasks,
        defaultLastDateForTasks: formData.defaultLastDateForTasks,
        contentSummary: formData.contentSummary,
        imgHash: formData.coverImg,
        actualGrade: formData.actualGradeId,
        suggestedGrade: formData.suggestedGradeId,
        coverId: uploadedImage?.file ? null : this.builder?.subject?.coverId
      };
      const response = await this.$Courses.Services.getSubjectBuilder({
        templateId: this.templateId, 
        lessons: formData.existChanges ? null : this.builder?.lessons,
        workingDays: formData.existChanges ? null : this.workingDays,
        specialDays: formData.existChanges ? null : this.specialDays,
        subject: this.template,
        coverImage: uploadedImage?.file,
        schoolDay: formData.existChanges ? { schoolDayId: formData.schoolDay} : this.schoolDay,
        existChanges: formData.existChanges,
        step: this.step,
        existFunLessons: formData.existChanges ? null : this.builder?.existFunLessons,
        additionalLessons: formData.existChanges ? null : this.builder?.additionalLessons,
        automaticFolderType: formData.automaticFolderType,
        componentsSelected: formData.componentsSelected,
        componentsChanged: formData.componentsChanged,
        quickContentId: this.quickContentId,
      });
      
      this.builder = { ...this.builder, ...response }
      this.callBackup.prevResourceId = this.builder.resourceId;

      this.status.setLoad();
    } catch(err) {
      this.status.setError();
    }
  }

  changeMenuTab(tabId) {
    if(!this.disabledSubjectForm && this.builder.schoolDay.entityId) {
      this.step = tabId;
      this.changeActiveTab(tabId);
    }
  }

  changeActiveTab(tabId) {
    this.menu = this.menu.map(m => ({...m, active: m.id === tabId}));
    if(tabId == 2) this.calendarType = this.calendarTypes.CALENDAR;
    else if (tabId == 3) this.calendarType = this.calendarTypes.LESSONS;
    else this.calendarType = '';

    const container = document.querySelector('body');
    setTimeout(() => {
      container?.scrollTo({ top: 0 });
    }, 200);
  }

  openConfigWorkingDays() {
    this.$edEventBus.$emit('openConfigWorkingDays')
  }

  async runRule(rule, rulesApplied = null) {
    try {  
      this.status.setLoading();

      this.closeSidebar();

      let specialDays = this.specialDays
      if(rule?.type == BUILDER_RULES.START_LESSON) {
        specialDays = this.specialDays.filter(
          el => !el.addedByRule
        );
      }

      const response = await this.$Courses.Services.getSubjectBuilder({
        templateId: this.templateId, 
        schoolDay: this.schoolDay,
        workingDays: this.workingDays,
        specialDays,
        subject: this.subject,
        coverImage: this.coverImage,
        rule: rule ? rule : null,
        lessons: this.lessons,
        existFunLessons: this.existFunLessons,
        additionalLessons: this.additionalLessons,
        resourceId: this.callBackup.prevResourceId == this.builder.resourceId ? null : this.callBackup.prevResourceId,
        rulesApplied: rulesApplied ? rulesApplied : this.rulesApplied,
        objectsTagged: this.objectsTagged,
        automaticFolderType: this.automaticFolderType,
        componentsSelected: this.componentsSelected,
        quickContentId: this.quickContentId,
      });
      this.callBackup.prevResourceId = this.builder.resourceId;
      this.builder = { ...this.builder, ...response };
      this.callBackup.funDayoffBackup = false;
      this.callBackup.teachingDaysBackup = false;

      this.status.setLoad();
    } catch(err) {
      this.status.setError();
    }
  }

  setUpdateRule({rule}) {
    this.toggleAddRules();
    this.ruleOptions = rule;
  }

  updateRule(rule) {
    const rulesApplied = this.rulesApplied.filter(el => el !== this.ruleOptions);
    this.runRule(rule, rulesApplied);
    this.ruleOptions = null;
  }

  removeRule({rule}) {
    let repeatedRules = [];
    if(rule.type === BUILDER_RULES.START_LESSON)
      repeatedRules = this.rulesApplied.filter(el => el.type === BUILDER_RULES.START_LESSON && el !== rule);
    else 
      repeatedRules = this.rulesApplied.filter(el => el.label === rule.label && el !== rule);

    let runRule
    if(repeatedRules.length > 0 && rule.active) {
      runRule = repeatedRules[repeatedRules.length-1];
    } else if(repeatedRules.length > 0 && !rule.active) {
      runRule = null;
    } else {
      if(rule.type === BUILDER_RULES.START_LESSON)
        runRule = { type: BUILDER_RULES.REMOVE_START_LESSON }
      else
        runRule = { type: BUILDER_RULES.REMOVE_PLACE_LABEL, label: rule.label };
    }

    const rulesApplied = this.rulesApplied.filter(el => el !== rule && el !== runRule);
    this.runRule(runRule, rulesApplied);
  }

  reRunRule({rule}) {
    const rulesApplied = this.rulesApplied.filter(el => el !== rule);
    this.runRule(rule, rulesApplied);
  } 

  toggleAddRules() {
    this.addRule = !this.addRule;
    this.ruleOptions = null;
  }

  openSidebar() {
    this.addRule = false;
    this.isSidebarOpened = true;
  }

  closeSidebar() {
    this.isSidebarOpened = false;
  }

  onUpdateLessonDates(lessonDate) {
    if (this.builder.lessons) {
      const lessonsUpdated = this.builder.lessons.map(l => {
        if (l.id === lessonDate.id) {
          return {
            ...l,
            schoolDate: lessonDate.schoolDate,
          }
        }

        return l;
      });
      
      this.builder.lessons = lessonsUpdated;
    }
  }

  async onSaveTeachingDays(data) {
    if(this.callBackup.teachingDaysBackup && JSON.stringify(this.callBackup.prevTeachingDays) === JSON.stringify(data.workingDays)) {
      this.getBuilderBackup();
    } else {
      try {
        this.status.setLoading();
        const response = await this.$Courses.Services.getSubjectBuilder({
          templateId: this.templateId, 
          schoolDay: this.schoolDay, 
          workingDays: data.workingDays,
          specialDays: this.specialDays,
          subject: this.subject,
          coverImage: this.coverImage,
          lessons: this.lessons,
          existFunLessons: this.existFunLessons,
          additionalLessons: this.additionalLessons,
          resourceId: this.callBackup.prevResourceId == this.builder.resourceId ? null : this.callBackup.prevResourceId,
          rulesApplied: this.rulesApplied,
          objectsTagged: this.objectsTagged,
          automaticFolderType: this.automaticFolderType,
          componentsSelected: this.componentsSelected,
          quickContentId: this.quickContentId,
        });
        this.callBackup.prevResourceId = this.builder.resourceId;
        this.builder = { ...this.builder, ...response }
        this.callBackup.funDayoffBackup= false;
        this.callBackup.teachingDaysBackup = true;
        this.callBackup.prevTeachingDays = data.prevWorkingDays;

        this.status.setLoad();

        if(response.overContentLessons) {
          this.$EdModal.createConfirmationModal({
            title: this.$i18n.t('courses.subjectBuilder.info.overflowLessonsTitle'),
            body: this.$i18n.t('courses.subjectBuilder.info.overflowLessons'),
            action: () => this.closeModal(),
            actionText: this.$i18n.t('common.ok'),
            secondAction: () => this.getBuilderBackup(),
            secondActionText: this.$i18n.t('courses.subjectBuilder.undo')
          });
        }
      } catch(err) {
        this.status.setError();
        this.calendarComponent.goToPresentDay();
      }
    }
  }

  async onRefreshSubjectBuilder() {
    try {
      this.status.setLoading();
      const response = await this.$Courses.Services.getSubjectBuilder({
        templateId: this.templateId, 
        schoolDay: this.schoolDay, 
        workingDays: this.workingDays,
        specialDays: this.specialDays,
        subject: this.subject,
        coverImage: this.coverImage,
        lessons: this.lessons,
        existFunLessons: this.existFunLessons,
        additionalLessons: this.additionalLessons,
        resourceId: this.callBackup.prevResourceId == this.builder.resourceId ? null : this.callBackup.prevResourceId,
        rulesApplied: this.rulesApplied,
        objectsTagged: this.objectsTagged,
        automaticFolderType: this.automaticFolderType,
        componentsSelected: this.componentsSelected,
        quickContentId: this.quickContentId,
      });
      this.callBackup.prevResourceId = this.builder.resourceId;
      this.builder = { ...this.builder, ...response }

      this.status.setLoad();
    } catch(err) {
      this.status.setError();
    }
  }

  async onSetSpecialDays({ specialDays, rule, option, prevOption, date }) {
    if(this.callBackup.funDayoffBackup && (rule?.type === BUILDER_RULES.REMOVE_FUN_LESSON || !rule) 
      && option === BUILDER_DAY_OPTIONS.IGNORED && this.callBackup.backupDate === date) { 
      this.getBuilderBackup();
    } else {
      try {  
        this.status.setLoading();
        const response = await this.$Courses.Services.getSubjectBuilder({
          templateId: this.templateId, 
          schoolDay: this.schoolDay,
          workingDays: this.workingDays,
          specialDays: specialDays,
          subject: this.subject,
          coverImage: this.coverImage,
          rule: rule ? rule : null,
          lessons: this.lessons,
          existFunLessons: this.existFunLessons,
          additionalLessons: this.additionalLessons,
          resourceId: this.callBackup.prevResourceId == this.builder.resourceId ? null : this.callBackup.prevResourceId,
          rulesApplied: this.rulesApplied,
          objectsTagged: this.objectsTagged,
          automaticFolderType: this.automaticFolderType,
          componentsSelected: this.componentsSelected,
          quickContentId: this.quickContentId,
        });
        this.callBackup.prevResourceId = this.builder.resourceId;
        this.builder = { ...this.builder, ...response };
        this.callBackup.teachingDaysBackup = false;
        this.status.setLoad();  

        // callBackup is true when a fun or day off is added
        if(rule?.type === BUILDER_RULES.ADD_FUN_LESSON || !rule) { 
          this.callBackup.funDayoffBackup= true;
          this.callBackup.backupDate = date;
        } else this.callBackup.funDayoffBackup= false;

        // callBackup is false when moving from day off to fun or viceversa
        if((prevOption === BUILDER_DAY_OPTIONS.OBSERVED && option === BUILDER_DAY_OPTIONS.FUN) 
        || (prevOption === BUILDER_DAY_OPTIONS.FUN && option === BUILDER_DAY_OPTIONS.OBSERVED)) 
          this.callBackup.funDayoffBackup= false;

        if(response.overContentLessons && (!rule || rule?.type === BUILDER_RULES.ADD_FUN_LESSON)) {
          this.$EdModal.createConfirmationModal({
            title: this.$i18n.t('courses.subjectBuilder.info.overflowLessonsTitle'),
            body: this.$i18n.t('courses.subjectBuilder.info.overflowLessons'),
            action: () => this.closeModal(),
            actionText: this.$i18n.t('common.ok'),
            secondAction: () => this.getBuilderBackup(),
            secondActionText: this.$i18n.t('courses.subjectBuilder.undo')
          });
        }
      } catch(err) {
        this.status.setError();
        this.calendarComponent.goToPresentDay();
      }
    }
  }

  async setAllLessons() {
    this.builder.allLessons = await this.$Courses.Services.getBuilderAllLessons(
      this.templateId,
      BUILDER_EVENT_TYPES.CONTENT
    );
  }

  async getBuilderBackup() {
    try {
      this.status.setLoading();
      const response = await this.$Courses.Services.getBuilderBackup(this.templateId, this.callBackup.prevResourceId);
      this.builder = { ...this.builder, ...response };
      this.callBackup.funDayoffBackup= false;
      this.callBackup.teachingDaysBackup= false;
      this.status.setLoad();
    } catch(err) {
      this.status.setError();
      this.calendarComponent.goToPresentDay();
    }
  }

  closeModal() {
    this.$EdModal.closeModal();
  }

  mounted() {
    document.querySelector('body').style.overflow = 'auto';
    
    this.initFlow();
  }

  async initFlow() {
    await this.getTemplateDescription();
    await this.setAllLessons();
  }

  created() {
    this.$edEventBus.$on(COURSE_TEMPLATE_EVENTS.SAVE_COURSE_FORM, this.saveSubjectDescription);
    this.menu = BUILDER_MENU.map((m: any) => {
      return new EdTabsHeaderOption({
        ...m,
        label: this.$i18n.t(`courses.subjectBuilder.${m.label}`),
      });
    });
    this.status.setLoading();
  }

  beforeDestroy() {
    this.$edEventBus.$off(COURSE_TEMPLATE_EVENTS.SAVE_COURSE_FORM);
  }

  @Watch('step', { deep: true }) 
  showWarningNoLessons() {
    if (this.step == 3 && this.lessons.length == 0) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.subjectBuilder.errors.emptyTemplate'),
        body: this.$i18n.t('courses.subjectBuilder.errors.emptyTemplateBody'),
        type: KEYS.EVENT_TYPES.WARNING
      });
    }
  }

}